import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams, useLocation, Navigate} from "react-router-dom";

import {APPCONFIG} from '../../app.config';
import {Loading, SectionHeading, SectionSidebar} from '../../components/common';
import FirmaDigitalAction from '../../components/controls/FirmaDigitalAction';
import {useForm, useFormErrors, useNav, useUnsavedChangesGuard} from '../../components/hooks';
import {useEntidad} from '../../components/hooks/useEntidad';
import {useReporte} from '../../components/hooks/useReporte';
import { ALERT_TYPE, CUENTA_STATE, FIRMA_TYPE, OPERATION_MODE, ShowToastMessage, dataTaggerActionClear, dataTaggerActionSet } from '@laramie/ui';
import {VALIDATION_MESSAGE} from '../../consts/validationMessage';
import {getDateId, getDateNow} from '../../utils/convert';
import {CloneObject, CompareDates, OpenObjectURL} from '../../utils/helpers';
import {BajaVehiculoModal, MenuLateral, SectionForm} from './components';
import {
    AddVehiculo,
    DominioRegExps,
    FindVehiculo,
    MakeListDomiciliosRelacionados,
    ModifyBajaCuenta,
    ModifyVehiculo,
    buildVehiculoEntityInit,
    UpdateEntityRow
} from './utils';
import {FooterButtonVolver} from '../../components/buttons/FooterButtonVolver/FooterButtonVolver';
import {PERMISSIONS} from '../../consts/permissions';

function VehiculoView() {

    //parametros url
    const params = useParams();
    const location = useLocation();
    const previousFilters = location.state?.filters;

    const [vehiculoMenor, setVehiculoMenor] = useState(false);

    //#region Variables
    const entityInit = buildVehiculoEntityInit();
    const menuesInit = {
        expedientes: false,
        certificadosEscribanos: false,
        relacionesCuentas: false,
        controladoresCuentas: false,
        zonasEntrega: false,
        aplicacionCreditos: false,
        condonacion: false,
        pagosAnticipados: false,
        planesPago: false
    };
    //#endregion

    //#region Hooks
    const navigate = useNav()

    const [state, setState] = useState({
        processKey: `Vehiculo_${params.id ?? 0}_${getDateId()}`,
        id: params.id ? parseInt(params.id) : 0,
        mode: params.mode,
        loading: false,
        entity: entityInit,
        menues: menuesInit,
        showMenu: false,
        tabActive: "exenciones",
        showInfoVehiculoMenor: false
    });

    const [, getRowEntidad] = useEntidad({
        entidades: ['IncisoVehiculo'],
        onLoaded: (entidades, isSuccess, error) => {
            if (isSuccess) {
                setState(prevState => ({...prevState}));
            } else {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
            }
        },
        memo: {
            key: 'IncisoVehiculo',
            timeout: 0
        }
    });

    const [toggledSidebarLeft, setToggledSidebarLeft] = useState(true);
    const [pendingChange, setPendingChange] = useState(state.mode === OPERATION_MODE.NEW);
    const [showBajaModal, setShowBajaModal] = useState(false);

    const dispatch = useDispatch();
    const dataTagger = useSelector((state) => state.dataTagger.data);

    const {allowNavigation} = useUnsavedChangesGuard(pendingChange);
    const navigateAllowed = (navOptions) => {
        allowNavigation();
        navigate(navOptions);
    };

    const mount = () => {
        // alta obsoleta: en modo NEW el guard redirige a la grilla, no inicializar ni avisar
        if (state.mode === OPERATION_MODE.NEW) return;
        if (state.id > 0) {
            FindVehiculo(state, setState, callbackNoSuccess, callbackError, callbackSuccessFindVehiculo);
        } else {
            dispatch(dataTaggerActionSet(state.processKey, {
                Archivo: [],
                Observacion: [],
                Etiqueta: []
            }));
            ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'Ingrese los datos y presione siguiente para confirmar el alta');
        }
    }
    useEffect(mount, []);

    const [listExpedientes, setListExpedientes] = useState([]);

    const updateListExpedientes = () => {
        var list = []
        const listsWithExpediente = [
            ...state.entity.recargosDescuentos
        ];
        listsWithExpediente.filter(f => f.state !== 'r').forEach(x => {
            if (x.idExpediente && x.idExpediente > 0 && list.indexOf(x.idExpediente) === -1) list.push(x.idExpediente)
        });

        setListExpedientes(list);
    }

    useEffect(updateListExpedientes, [state.entity.recargosDescuentos]);


    const [listDireccionesRelacionadas, setListDireccionesRelacionadas] = useState([]);
    const updateListDireccionesRelacionadas = () => {
        MakeListDomiciliosRelacionados(state.entity.vinculosVehiculo)
            .then(list => {
                setListDireccionesRelacionadas(list);
            })
            .catch((error) => {
                const message = `Error consultando domicilios: ${error.message}`;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            });
    }

    useEffect(updateListDireccionesRelacionadas, [state.entity.vinculosVehiculo]);


    const [vehiculo_formValues, vehiculo_formHandle, , vehiculo_formSet] = useForm({
        idEstadoCarga: 20,
        dominio: '',
        dominioAnterior: '',
        anioModelo: '',
        marca: '',
        codigoMarca: '',
        modelo: '',
        idIncisoVehiculo: 0,
        idTipoVehiculo: 0,
        idCategoriaVehiculo: 0,
        numeroMotor: '',
        marcaMotor: '',
        numeroChasis: '',
        serieMotor: '',
        legajo: '',
        valuacion: 0,
        peso: '',
        carga: '',
        cilindrada: '',
        idOrigenFabricacion: 0,
        idCombustible: 0,
        idUsoVehiculo: 0,
        idMotivoBajaVehiculo: 0,
        recupero: '',
        radicacionAnterior: '',
        fechaAlta: null,
        fechaPatentamiento: null,
        fechaRadicacion: null,
        fechaTransferencia: null,
        fechaCompra: null,
        fechaBaja: null,
        fechaHabilitacionDesde: null,
        fechaHabilitacionHasta: null,
        fechaDDJJ: null,
        codigoMTM: ''
    }, 'vehiculo_');

    const [cuenta_formValues, cuenta_formHandle, , cuenta_formSet] = useForm({
        numeroCuenta: '',
        numeroWeb: '',
        idEstadoCuenta: 0
    }, 'cuenta_');

    const [generateReporte,] = useReporte({
        callback: (reporte, buffer, message) => {
            if (buffer)
                OpenObjectURL(`${reporte}.pdf`, buffer);
            else
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        }
    });
    //#endregion

    const today = useMemo(() => getDateNow(), []);

    const CAMPOS_DATOS_VEHICULO = ['idIncisoVehiculo', 'idTipoVehiculo', 'anioModelo', 'dominio', 'idCategoriaVehiculo', 'marca', 'modelo', 'cilindrada', 'idUsoVehiculo'];
    const CAMPOS_ADMINISTRATIVOS = ['fechaAlta', 'fechaPatentamiento', 'fechaRadicacion', 'fechaTransferencia', 'fechaCompra', 'fechaDDJJ', 'fechaBaja', 'fechaHabilitacionHasta'];
    const [datosOpen, setDatosOpen] = useState(false);
    const [administrativosOpen, setAdministrativosOpen] = useState(false);
    const [estadoOpen, setEstadoOpen] = useState(false);
    const { errorOf, validate } = useFormErrors(getErrors, getFormatErrors);

    //#region Handles
    const cuenta_formHandleProxy = (event) => {
        cuenta_formHandle(event);
        setPendingChange(true);
    }
    const vehiculo_formHandleProxy = (event) => {
        vehiculo_formHandle(event);
        setPendingChange(true);
    }
    const handleClickGuardar = () => {
        const errors = { ...getErrors(), ...getFormatErrors() };
        if (CAMPOS_DATOS_VEHICULO.some(campo => errors[campo])) setDatosOpen(true);
        if (CAMPOS_ADMINISTRATIVOS.some(campo => errors[campo])) setAdministrativosOpen(true);
        if (errors.idEstadoCarga) setEstadoOpen(true);
        if (!validate()) return;

        if (state.id === 0) {
            AddVehiculo(state, setState, vehiculo_formValues, cuenta_formValues, dispatch, navigateAllowed, dataTaggerActionClear, callbackNoSuccess, callbackError)
        } else {
            ModifyVehiculo(state, setState, dataTagger, vehiculo_formValues, cuenta_formValues, dispatch, dataTaggerActionClear, callbackNoSuccess, callbackError, callbackSuccessFindVehiculo);
        }
    };

    const handleClickBaja = () => {
        setShowBajaModal(true);
    }

    const handleClickReporte = (reporte) => {
        const paramsReporte = {
            idVehiculo: state.id
        }
        generateReporte(reporte, paramsReporte);
    }

    const handleChangeInciso = (event) => {
        const {target} = event;
        let idIncisoVehiculo = 0;
        let showInfoVehiculoMenor = false;
        if (target.row) {
            idIncisoVehiculo = parseInt(target.value);
            showInfoVehiculoMenor = target.row.vehiculoMenor;
        }
        setVehiculoMenor(showInfoVehiculoMenor)

        vehiculo_formSet({
            ...vehiculo_formValues,
            idIncisoVehiculo: idIncisoVehiculo,
            idTipoVehiculo: 0,
            idCategoriaVehiculo: 0,
            dominioAnterior: (showInfoVehiculoMenor) ? '' : vehiculo_formValues.dominioAnterior,
            codigoMarca: (showInfoVehiculoMenor) ? '' : vehiculo_formValues.codigoMarca,
            peso: (showInfoVehiculoMenor) ? '' : vehiculo_formValues.peso,
            carga: (showInfoVehiculoMenor) ? '' : vehiculo_formValues.carga,
            cilindrada: (!showInfoVehiculoMenor) ? '' : vehiculo_formValues.cilindrada
        });
        setState(prevState => {
            return {...prevState, showInfoVehiculoMenor: showInfoVehiculoMenor};
        });
        setPendingChange(true);
    };
    //#endregion

    //#region Callbacks
    const callbackNoSuccess = (response) => {
        response.json()
            .then((error) => {
                const message = error.message;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return {...prevState, loading: false};
                });
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return {...prevState, loading: false};
                });
            });
    }
    const callbackError = (error) => {
        const message = 'Error procesando solicitud: ' + error.message;
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        setState(prevState => {
            return {...prevState, loading: false};
        });
    }
    const callbackSuccessFindVehiculo = (response) => {
        response.json()
            .then((data) => {

                //deserialize date fields
                if (data.vehiculo.fechaCargaInicio) data.vehiculo.fechaCargaInicio = new Date(data.vehiculo.fechaCargaInicio);
                if (data.vehiculo.fechaCargaFin) data.vehiculo.fechaCargaFin = new Date(data.vehiculo.fechaCargaFin);
                if (data.vehiculo.fechaAlta) data.vehiculo.fechaAlta = new Date(data.vehiculo.fechaAlta);
                if (data.vehiculo.fechaPatentamiento) data.vehiculo.fechaPatentamiento = new Date(data.vehiculo.fechaPatentamiento);
                if (data.vehiculo.fechaRadicacion) data.vehiculo.fechaRadicacion = new Date(data.vehiculo.fechaRadicacion);
                if (data.vehiculo.fechaTransferencia) data.vehiculo.fechaTransferencia = new Date(data.vehiculo.fechaTransferencia);
                if (data.vehiculo.fechaCompra) data.vehiculo.fechaCompra = new Date(data.vehiculo.fechaCompra);
                if (data.vehiculo.fechaBaja) data.vehiculo.fechaBaja = new Date(data.vehiculo.fechaBaja);
                if (data.vehiculo.fechaHabilitacionDesde) data.vehiculo.fechaHabilitacionDesde = new Date(data.vehiculo.fechaHabilitacionDesde);
                if (data.vehiculo.fechaHabilitacionHasta) data.vehiculo.fechaHabilitacionHasta = new Date(data.vehiculo.fechaHabilitacionHasta);
                if (data.vehiculo.fechaDDJJ) data.vehiculo.fechaDDJJ = new Date(data.vehiculo.fechaDDJJ);

                if (data.cuenta.fechaAlta) data.cuenta.fechaAlta = new Date(data.cuenta.fechaAlta);
                if (data.cuenta.fechaBaja) data.cuenta.fechaBaja = new Date(data.cuenta.fechaBaja);

                data.archivos.forEach(x => {
                    if (x.fecha) x.fecha = new Date(x.fecha);
                });
                data.observaciones.forEach(x => {
                    if (x.fecha) x.fecha = new Date(x.fecha);
                });
                data.variablesCuenta.forEach(x => {
                    if (x.fechaDesde) x.fechaDesde = new Date(x.fechaDesde);
                    if (x.fechaHasta) x.fechaHasta = new Date(x.fechaHasta);
                });
                data.vinculosVehiculo.forEach(x => {
                    if (x.fechaInstrumentoDesde) x.fechaInstrumentoDesde = new Date(x.fechaInstrumentoDesde);
                    if (x.fechaInstrumentoHasta) x.fechaInstrumentoHasta = new Date(x.fechaInstrumentoHasta);
                });
                data.condicionesEspeciales.forEach(x => {
                    if (x.fechaDesde) x.fechaDesde = new Date(x.fechaDesde);
                    if (x.fechaHasta) x.fechaHasta = new Date(x.fechaHasta);
                });
                data.recargosDescuentos.forEach(x => {
                    if (x.fechaDesde) x.fechaDesde = new Date(x.fechaDesde);
                    if (x.fechaHasta) x.fechaHasta = new Date(x.fechaHasta);
                    if (x.fechaOtorgamiento) x.fechaOtorgamiento = new Date(x.fechaOtorgamiento);
                });
                data.debitosAutomaticos.forEach(x => {
                    if (x.fechaDesde) x.fechaDesde = new Date(x.fechaDesde);
                    if (x.fechaAlta) x.fechaAlta = new Date(x.fechaAlta);
                    if (x.fechaBaja) x.fechaBaja = new Date(x.fechaBaja);
                });
                setPendingChange(false);
                vehiculo_formSet({
                    idEstadoCarga: data.vehiculo.idEstadoCarga,
                    dominio: data.vehiculo.dominio,
                    dominioAnterior: data.vehiculo.dominioAnterior,
                    anioModelo: data.vehiculo.anioModelo,
                    marca: data.vehiculo.marca,
                    codigoMarca: data.vehiculo.codigoMarca,
                    modelo: data.vehiculo.modelo,
                    idIncisoVehiculo: data.vehiculo.idIncisoVehiculo,
                    idTipoVehiculo: data.vehiculo.idTipoVehiculo,
                    idCategoriaVehiculo: data.vehiculo.idCategoriaVehiculo,
                    numeroMotor: data.vehiculo.numeroMotor,
                    marcaMotor: data.vehiculo.marcaMotor,
                    numeroChasis: data.vehiculo.numeroChasis,
                    serieMotor: data.vehiculo.serieMotor,
                    legajo: data.vehiculo.legajo,
                    valuacion: data.vehiculo.valuacion,
                    peso: data.vehiculo.peso,
                    carga: data.vehiculo.carga,
                    cilindrada: data.vehiculo.cilindrada,
                    idOrigenFabricacion: data.vehiculo.idOrigenFabricacion,
                    idCombustible: data.vehiculo.idCombustible,
                    idUsoVehiculo: data.vehiculo.idUsoVehiculo,
                    idMotivoBajaVehiculo: data.vehiculo.idMotivoBajaVehiculo,
                    recupero: data.vehiculo.recupero,
                    radicacionAnterior: data.vehiculo.radicacionAnterior,
                    fechaAlta: data.vehiculo.fechaAlta,
                    fechaPatentamiento: data.vehiculo.fechaPatentamiento,
                    fechaRadicacion: data.vehiculo.fechaRadicacion,
                    fechaTransferencia: data.vehiculo.fechaTransferencia,
                    fechaCompra: data.vehiculo.fechaCompra,
                    fechaBaja: data.vehiculo.fechaBaja,
                    fechaHabilitacionDesde: data.vehiculo.fechaHabilitacionDesde,
                    fechaHabilitacionHasta: data.vehiculo.fechaHabilitacionHasta,
                    fechaDDJJ: data.vehiculo.fechaDDJJ,
                    codigoMTM: data.vehiculo.codigoMTM
                });

                let showInfoVehiculoMenor = false;
                if (data.vehiculo.idIncisoVehiculo !== 0) {
                    const row = getRowEntidad('IncisoVehiculo', data.vehiculo.idIncisoVehiculo);
                    showInfoVehiculoMenor = (row) ? row.vehiculoMenor : false;
                }
                setVehiculoMenor(showInfoVehiculoMenor);

                setState(prevState => {
                    return {...prevState, loading: false, entity: data, showInfoVehiculoMenor: showInfoVehiculoMenor};
                });

                cuenta_formSet({
                    numeroCuenta: data.cuenta.numeroCuenta,
                    numeroWeb: data.cuenta.numeroWeb,
                    idEstadoCuenta: data.cuenta.idEstadoCuenta
                });
                dispatch(dataTaggerActionSet(state.processKey, {
                    Archivo: data.archivos,
                    Observacion: data.observaciones,
                    Etiqueta: data.etiquetas
                }));
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return {...prevState, loading: false};
                });
            });
    };
    //#endregion

    //#region Funciones
    function getErrors() {
        const errors = {};

        if (vehiculo_formValues.idEstadoCarga === 0) errors.idEstadoCarga = VALIDATION_MESSAGE.REQUIRED_SHORT;

        if (vehiculo_formValues.idIncisoVehiculo <= 0) errors.idIncisoVehiculo = VALIDATION_MESSAGE.REQUIRED_OPTION;
        if (vehiculo_formValues.idTipoVehiculo <= 0) errors.idTipoVehiculo = VALIDATION_MESSAGE.REQUIRED_SHORT;

        if (vehiculo_formValues.anioModelo.length === 0) errors.anioModelo = VALIDATION_MESSAGE.REQUIRED_SHORT;

        // El dominio es de solo lectura en la edición, así que sólo se valida en el alta o si el
        // valor cambió: los padrones migrados traen formatos históricos (y vehículos sin dominio)
        // que no tienen por qué bloquear el guardado del resto de la ficha.
        const dominioPersistido = state.entity.vehiculo.dominio;
        const dominioCambio = vehiculo_formValues.dominio !== dominioPersistido;
        if (state.mode === OPERATION_MODE.NEW || dominioCambio) {
            if (vehiculo_formValues.dominio.length === 0) errors.dominio = VALIDATION_MESSAGE.REQUIRED_SHORT;
            else {
                const dominio = vehiculo_formValues.dominio
                if (
                    (vehiculoMenor && !DominioRegExps.MOTO.test(dominio))
                    ||
                    (!vehiculoMenor && !DominioRegExps.AUTO.test(dominio))
                ) {
                    errors.dominio = 'Formato incorrecto';
                }
            }
        }

        if (state.mode === OPERATION_MODE.EDIT) {
            if (vehiculo_formValues.idCategoriaVehiculo <= 0) errors.idCategoriaVehiculo = VALIDATION_MESSAGE.REQUIRED_SHORT;
            if (vehiculo_formValues.marca.length === 0) errors.marca = VALIDATION_MESSAGE.REQUIRED_SHORT;
            if (vehiculo_formValues.modelo.length === 0) errors.modelo = VALIDATION_MESSAGE.REQUIRED;
            if ((state.showInfoVehiculoMenor) && (vehiculo_formValues.cilindrada.length === 0)) errors.cilindrada = VALIDATION_MESSAGE.REQUIRED_SHORT;
            if (vehiculo_formValues.idUsoVehiculo <= 0) errors.idUsoVehiculo = VALIDATION_MESSAGE.REQUIRED_SHORT;
            if (vehiculo_formValues.fechaAlta != null &&
                CompareDates(vehiculo_formValues.fechaAlta, today) > 0) errors.fechaAlta = 'Posterior a hoy';
            if (vehiculo_formValues.fechaPatentamiento != null &&
                CompareDates(vehiculo_formValues.fechaPatentamiento, today) > 0) errors.fechaPatentamiento = 'Posterior a hoy';
            if (vehiculo_formValues.fechaRadicacion != null &&
                CompareDates(vehiculo_formValues.fechaRadicacion, today) > 0) errors.fechaRadicacion = 'Posterior a hoy';
            if (vehiculo_formValues.fechaTransferencia != null &&
                CompareDates(vehiculo_formValues.fechaTransferencia, today) > 0) errors.fechaTransferencia = 'Posterior a hoy';
            if (vehiculo_formValues.fechaCompra != null &&
                CompareDates(vehiculo_formValues.fechaCompra, today) > 0) errors.fechaCompra = 'Posterior a hoy';
            if (vehiculo_formValues.fechaDDJJ != null &&
                CompareDates(vehiculo_formValues.fechaDDJJ, today) > 0) errors.fechaDDJJ = 'Posterior a hoy';
            if (vehiculo_formValues.fechaAlta != null && vehiculo_formValues.fechaBaja != null &&
                CompareDates(vehiculo_formValues.fechaBaja, vehiculo_formValues.fechaAlta) < 0) errors.fechaBaja = 'Anterior al alta';
            if (vehiculo_formValues.fechaHabilitacionDesde != null && vehiculo_formValues.fechaHabilitacionHasta != null &&
                CompareDates(vehiculo_formValues.fechaHabilitacionHasta, vehiculo_formValues.fechaHabilitacionDesde) < 0) errors.fechaHabilitacionHasta = 'Anterior al desde';
        }

        return errors;
    }

    function getFormatErrors() {
        const errors = {};

        if (vehiculo_formValues.anioModelo.length > 0) {
            const anioModeloEntero = parseInt(vehiculo_formValues.anioModelo, 10);
            const anioModeloPersistido = state.entity.vehiculo?.anioModelo ?? '';
            const anioModeloCambio = vehiculo_formValues.anioModelo !== anioModeloPersistido;
            if (isNaN(anioModeloEntero)) errors.anioModelo = 'Debe ser numérico';
            else if (state.mode === OPERATION_MODE.NEW || anioModeloCambio) {
                const minAnioModelo = 1900;
                const maxAnioModelo = today.getFullYear() + 1;
                if (anioModeloEntero < minAnioModelo || anioModeloEntero > maxAnioModelo) {
                    errors.anioModelo = `Entre ${minAnioModelo} y ${maxAnioModelo}`;
                }
            }
        }

        return errors;
    }

    function UpdateEntity(typeEntity, row) {
        // o:original a:add m:modify r:remove c:modify only childs
        let entity = CloneObject(state.entity);

        UpdateEntityRow(typeEntity, row, entity);

        setState(prevState => {
            return {...prevState, entity: entity};
        });
        setPendingChange(true);
    }

    function SetTabActive(tab) {
        setState(prevState => {
            return {...prevState, tabActive: tab};
        });
    }

    function ShowPopupMenu(menu) {
        let menues = CloneObject(menuesInit);
        menues[menu] = true;
        setState(prevState => {
            return {...prevState, showMenu: true, menues: menues};
        });
    }

    function HidePopupMenu() {
        setState(prevState => {
            return {...prevState, showMenu: false, menues: menuesInit};
        });
    }

    function ShowPage(partialUrl) {
        const url = APPCONFIG.SITE.WEBAPP + partialUrl + '/' + state.mode + '/' + state.entity.cuenta.id;
        window.open(url, '_blank');
    }

    function refetchVehiculo() {
        setState(x => ({...x, loading: true}))
        setTimeout(() => {
            FindVehiculo(state, setState, callbackNoSuccess, callbackError, callbackSuccessFindVehiculo)
        }, 2000)
    }

    //#endregion

    // La ficha sale completa y sin diálogo de opciones (contrato del Epic LAR-2347),
    // así que el menú la descarga directo en vez de abrir un modal.
    const [generateFicha, ] = useReporte({
        callback: (reporte, buffer, message, filename) => {
            if (buffer)
                OpenObjectURL(filename ?? `${reporte}.pdf`, buffer);
            else
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        }
    });

    const listMenu = [

        {
            menu: '1', items: [
                {
                    title: 'Expedientes', icon: 'draft', onClick: () => {
                        ShowPopupMenu('expedientes')
                    }
                },
                {
                    title: 'Ficha del vehículo', icon: 'description', onClick: () => {
                        generateFicha('CuentaFicha', {idCuenta: state.entity.cuenta.id})
                    }
                }
            ]
        },

        {
            menu: '2', items: [
                {
                    title: 'Gestión', icon: 'gavel', items: [
                        {
                            title: 'Certificados escribanos', onClick: () => {
                                ShowPopupMenu('certificadosEscribanos')
                            }
                        },
                        {
                            title: 'Relaciones', onClick: () => {
                                ShowPopupMenu('relacionesCuentas')
                            }
                        },
                        {
                            title: 'Controladores', onClick: () => {
                                ShowPopupMenu('controladoresCuentas')
                            }
                        },
                        {
                            title: 'Zonas entregas', onClick: () => {
                                ShowPopupMenu('zonasEntrega')
                            }
                        }
                    ]
                },
                {
                    title: 'Movimientos', icon: 'calculate', items: [
                        {
                            title: 'Cuenta corriente', onClick: () => {
                                ShowPage('cuenta-corriente/cuenta')
                            }, permisos: [PERMISSIONS.CUENTA_CORRIENTE_VIEW]
                        },
                        {
                            title: 'Créditos', onClick: () => {
                                ShowPopupMenu('aplicacionCreditos')
                            }, permisos: [PERMISSIONS.CUENTA_PAGO_EDIT, PERMISSIONS.CREDITO_EDIT]
                        },
                        {
                            title: 'Condonaciones', onClick: () => {
                                ShowPopupMenu('condonacion')
                            }, permisos: [PERMISSIONS.CONDONACION_EDIT]
                        },
                        {
                            title: 'Deuda', onClick: () => {
                                ShowPage('deuda')
                            }, permisos: [PERMISSIONS.CUENTA_PAGO_VIEW]
                        },
                        {
                            title: 'Planes de pago', onClick: () => {
                                ShowPopupMenu('planesPago')
                            }, permisos: [PERMISSIONS.CUENTA_PAGO_VIEW]
                        },
                        {
                            title: 'Liquidaciones', onClick: () => {
                                ShowPopupMenu('liquidaciones')
                            }, permisos: [PERMISSIONS.EMISION_EDIT, PERMISSIONS.LIQUIDACION_VIEW]
                        },
                        {
                            // pago-derecho-especial/:idCuenta no sigue el esquema de ShowPage (partialUrl/mode/id), por eso usamos window.open directo.
                            title: 'Derechos especiales', onClick: () => {
                                window.open(`${APPCONFIG.SITE.WEBAPP}pago-derecho-especial/${state.entity.cuenta.id}`, '_blank')
                            }, permisos: [PERMISSIONS.CUENTA_PAGO_EDIT]
                        },
                        {
                            title: 'Informe de deuda', onClick: () => {
                                ShowPopupMenu('informeDeuda')
                            }, permisos: [PERMISSIONS.CUENTA_PAGO_VIEW]
                        }
                    ]
                }
            ]
        },

    ];

    // El alta es por modal desde la grilla; /vehiculo/new queda obsoleta.
    if (state.mode === OPERATION_MODE.NEW) return <Navigate to={'/vehiculos' + location.search} replace />;

    return (
        <>

            <Loading visible={state.loading}></Loading>

            {showBajaModal && (
                <BajaVehiculoModal
                    show={showBajaModal}
                    fechaAlta={vehiculo_formValues.fechaAlta}
                    fechaBaja={vehiculo_formValues.fechaBaja}
                    idMotivoBajaVehiculo={vehiculo_formValues.idMotivoBajaVehiculo}
                    onDismiss={() => setShowBajaModal(false)}
                    onConfirm={(fechaBaja, idMotivoBajaVehiculo) => {
                        setShowBajaModal(false);
                        ModifyBajaCuenta(state.entity.cuenta.id, {fechaBaja, idMotivoBajaVehiculo}, state, setState, dispatch, navigateAllowed, dataTaggerActionClear, callbackNoSuccess, callbackError);
                    }}
                />
            )}


            {state.showMenu && (
                <MenuLateral
                    state={state}
                    listExpedientes={listExpedientes}
                    listDireccionesRelacionadas={listDireccionesRelacionadas}
                    UpdateEntity={UpdateEntity}
                    HidePopupMenu={HidePopupMenu}
                />
            )}

            <div className='section-frames'>

                <div className='section-sidebar-left'>
                    <SectionSidebar
                        collapsed={toggledSidebarLeft}
                        listMenu={listMenu}
                    />
                </div>

                <div className='section-main'>

                    <SectionHeading
                        handleToggleSidebar={(state.mode !== OPERATION_MODE.NEW) ? () => {
                            setToggledSidebarLeft(prevState => !prevState)
                        } : null}
                        isSidebarOpen={!toggledSidebarLeft}
                        titles={[
                            {title: 'Tributos'},
                            {title: 'Administración de vehículos', url: '/vehiculos'},
                            {title: state.id === 0 ? 'Nuevo' : `Número de cuenta: ${state.entity.cuenta.numeroCuenta}`}
                        ]}
                    />

                    <SectionForm
                        SetTabActive={SetTabActive}
                        handleChangeInciso={handleChangeInciso}
                        pendingChange={pendingChange}
                        setPendingChange={setPendingChange}
                        state={state}
                        vehiculo_formValues={vehiculo_formValues}
                        vehiculo_formSet={vehiculo_formSet}
                        vehiculo_formHandleProxy={vehiculo_formHandleProxy}
                        cuenta_formValues={cuenta_formValues}
                        cuenta_formSet={cuenta_formSet}
                        cuenta_formHandleProxy={cuenta_formHandleProxy}
                        UpdateEntity={UpdateEntity}
                        handleClickBaja={handleClickBaja}
                        refetchVehiculo={refetchVehiculo}
                        errorOf={errorOf}
                        datosOpen={datosOpen}
                        setDatosOpen={setDatosOpen}
                        administrativosOpen={administrativosOpen}
                        setAdministrativosOpen={setAdministrativosOpen}
                        estadoOpen={estadoOpen}
                        setEstadoOpen={setEstadoOpen}
                    />

                </div>

            </div>

            <footer className='footer footer-action'>
                <div className='footer-action-container'>
                    <FooterButtonVolver pendingChange={pendingChange} processKey={state.processKey}
                                        filters={previousFilters} to="/vehiculos"
                                        allowNavigation={allowNavigation}/>
                    {state.mode !== OPERATION_MODE.VIEW && (
                        <button
                            className={pendingChange ? "btn action-button float-end" : "btn action-button btn-disabled float-end"}
                            onClick={(event) => handleClickGuardar()}
                            disabled={!pendingChange}
                        >
                            {(state.mode === OPERATION_MODE.NEW) ? "Siguiente" : "Guardar"}
                        </button>
                    )}
                </div>
            </footer>

        </>
    )
}

export default VehiculoView;


